/**
 * StoryPanel — LEFT-side hero panel on the sign-in page.
 *
 * Hidden on mobile (< md). Renders the tenant's brand mark, headline copy,
 * feature chips and stats footer. Colour-scheme swaps between customer
 * (gradient background) and staff (dark canvas with accent orbs) modes.
 */
import { Link } from "react-router-dom";
import {
  Fuel, ArrowRight,
  Truck, MapPin, Wallet, ShieldCheck,
  Command, Activity, FileText, Users,
} from "lucide-react";

const CUSTOMER_CHIPS = [
  { i: Truck, t: "Doorstep 60-min SLA" },
  { i: MapPin, t: "Live GPS tracking" },
  { i: Wallet, t: "Wallet + Razorpay" },
  { i: ShieldCheck, t: "PESO approved bowsers" },
];

const STAFF_CHIPS = [
  { i: Command, t: "Order & dispatch console" },
  { i: Activity, t: "Live GPS bowser tracking" },
  { i: FileText, t: "GST-ready invoices" },
  { i: Users, t: "Role-based access control" },
];

const CUSTOMER_STATS = [
  { v: "50K+", l: "Deliveries" },
  { v: "60 min", l: "Avg. arrival" },
  { v: "4.8★", l: "Rating" },
];

const STAFF_STATS = [
  { v: "24×7", l: "Uptime" },
  { v: "50K+", l: "Deliveries" },
  { v: "180+", l: "Bowsers" },
];

export function StoryPanel({ isStaff, brand, customerTheme, staffTheme, isStandalone }) {
  const panelStyle = isStaff
    ? { color: staffTheme.text }
    : {
        background: `linear-gradient(135deg, ${customerTheme.gradient_from}, ${customerTheme.gradient_to})`,
        color: customerTheme.text_on_gradient,
      };
  const mutedColor = isStaff ? staffTheme.muted_text : "rgba(255,255,255,0.85)";

  return (
    <div
      className="relative hidden md:flex md:flex-col md:justify-between md:overflow-hidden md:p-10 lg:p-14"
      style={panelStyle}
    >
      {/* Brand mark */}
      <Link to="/" className="relative z-10 flex items-center gap-3">
        {brand.logo_url ? (
          <img
            src={brand.logo_url}
            alt={brand.name}
            className="h-12 w-12 rounded-xl bg-white object-contain p-1 shadow-lg"
            data-testid="login-brand-logo"
          />
        ) : (
          <div
            className="grid h-12 w-12 place-items-center rounded-xl shadow-lg"
            style={isStaff
              ? { backgroundColor: staffTheme.accent, color: "#FFFFFF" }
              : { backgroundColor: "#FFFFFF", color: customerTheme.accent }}
          >
            <Fuel size={22} strokeWidth={2.5} />
          </div>
        )}
        <div>
          <div className="font-display text-xl font-black leading-none tracking-tight" data-testid="login-brand-name">
            {brand.name}
          </div>
          <div
            className="mt-1 text-[10px] font-bold uppercase tracking-[0.25em]"
            style={{ color: isStaff ? staffTheme.accent : "rgba(255,255,255,0.75)" }}
          >
            {isStaff ? brand.staff_login_tagline : brand.customer_login_tagline}
          </div>
        </div>
      </Link>

      {/* Story */}
      <div className="relative z-10 max-w-lg">
        {!isStandalone && (
          <Link
            to="/"
            className="mb-3 inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-widest opacity-80 hover:opacity-100 hover:underline"
            style={{ color: mutedColor }}
            data-testid="link-back-home-panel"
          >
            <ArrowRight size={12} className="rotate-180" /> Back to home
          </Link>
        )}
        <div
          className="mb-5 inline-flex items-center gap-2 rounded-full border px-3 py-1 text-[10px] font-bold uppercase tracking-[0.25em] backdrop-blur"
          style={isStaff
            ? { borderColor: "rgba(255,255,255,0.15)", background: "rgba(255,255,255,0.05)", color: staffTheme.accent }
            : { borderColor: "rgba(255,255,255,0.25)", background: "rgba(255,255,255,0.10)", color: customerTheme.text_on_gradient }}
        >
          <PulsingDot color={isStaff ? staffTheme.accent : customerTheme.text_on_gradient} />
          {isStaff ? "Restricted access · Staff & driver sign-in" : "Live · Trusted by 500+ businesses"}
        </div>

        <h1 className="font-display text-5xl font-black leading-[1.02] tracking-tight lg:text-6xl">
          {isStaff ? (
            <>
              Command your <br />
              <span
                className="bg-clip-text text-transparent"
                style={{ backgroundImage: `linear-gradient(90deg, ${staffTheme.accent}, ${staffTheme.accent_secondary})` }}
              >
                fuel operation.
              </span>
            </>
          ) : (
            <>Refuel your <br />business in minutes.</>
          )}
        </h1>

        <p className="mt-5 text-base" style={{ color: mutedColor }}>
          {isStaff
            ? "Dispatch orders, track bowsers on live GPS, close trips, and generate GST invoices. The integrated ERP built for administrators, dispatchers, drivers, and accounts."
            : "Order high-quality BS-VI diesel to your site. Live GPS tracking, instant GST invoices, wallet-based payments, and PESO-approved bowsers — all in one app."}
        </p>

        <div className="mt-8 grid grid-cols-2 gap-3">
          {(isStaff ? STAFF_CHIPS : CUSTOMER_CHIPS).map(({ i: Icon, t }) => (
            <FeatureChip key={t} label={t} Icon={Icon} isStaff={isStaff} customerTheme={customerTheme} staffTheme={staffTheme} />
          ))}
        </div>
      </div>

      {/* Stats footer */}
      <div className="relative z-10 grid grid-cols-3 gap-2 border-t pt-5" style={{ borderColor: "rgba(255,255,255,0.15)" }}>
        {(isStaff ? STAFF_STATS : CUSTOMER_STATS).map((s) => (
          <div key={s.l}>
            <div className="font-display text-2xl font-black">{s.v}</div>
            <div
              className="text-[10px] font-semibold uppercase tracking-widest"
              style={{ color: isStaff ? staffTheme.muted_text : "rgba(255,255,255,0.7)" }}
            >
              {s.l}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function PulsingDot({ color }) {
  return (
    <span className="relative flex h-1.5 w-1.5">
      <span className="absolute inline-flex h-full w-full animate-ping rounded-full opacity-75" style={{ backgroundColor: color }} />
      <span className="relative inline-flex h-1.5 w-1.5 rounded-full" style={{ backgroundColor: color }} />
    </span>
  );
}

function FeatureChip({ label, Icon, isStaff, customerTheme, staffTheme }) {
  const chipStyle = isStaff
    ? { borderColor: "rgba(255,255,255,0.10)", background: "rgba(255,255,255,0.05)" }
    : { borderColor: "rgba(255,255,255,0.20)", background: "rgba(255,255,255,0.10)" };
  const iconStyle = isStaff
    ? { backgroundColor: `${staffTheme.accent}33`, color: staffTheme.accent, boxShadow: `inset 0 0 0 1px ${staffTheme.accent}66` }
    : { backgroundColor: "rgba(255,255,255,0.25)", color: customerTheme.text_on_gradient };
  return (
    <div className="flex items-center gap-2.5 rounded-xl border px-3 py-2.5 text-sm font-medium backdrop-blur" style={chipStyle}>
      <span className="grid h-8 w-8 flex-shrink-0 place-items-center rounded-lg" style={iconStyle}>
        <Icon size={14} strokeWidth={2.5} />
      </span>
      {label}
    </div>
  );
}
